import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";
import { alpha } from "../../../theme/Mixins";
import { resolveEmphasisBorderWidth } from "../../../theme/choiceThemeTokens";
import { resolveFieldBorderWidth } from "../../../theme/inputThemeTokens";
import {
    MODERN_SLIDER_HANDLE_SIZE_PX,
    MODERN_SLIDER_TRACK_HEIGHT_PX,
    resolveModernSliderTrackBorderRadius,
    resolveSliderEndpointLabelColor,
    resolveSliderFocusRingShadow,
    resolveSliderHandleBorderColor,
    resolveSliderHandleFillColor,
    resolveSliderHandleLabelColor,
    resolveSliderHandleLabelFontWeight,
    resolveSliderTrackFilledColor,
    resolveSliderTrackUnfilledColor,
    useModernSliderStyles,
} from "../../../theme/sliderThemeTokens";

const railHeight = ({ theme, isHybridGrid }: { theme: any; isHybridGrid: boolean }) =>
    isHybridGrid ? theme.SliderRailHeightSmall : theme.SliderRailHeightLarge;
const handleSize = ({ theme, isHybridGrid }: { theme: any; isHybridGrid: boolean }) =>
    isHybridGrid ? theme.SliderHandleSizeSmall : theme.SliderHandleSizeLarge;

export default createUseStyles({
    labelContainer: {
        composes: "slider__label-container",
        display: "flex",
        flexDirection: "row",
    },
    label: {
        composes: "slider__label",
        flex: "auto",
        marginTop: ({ theme }: { theme: any }) => (useModernSliderStyles(theme) ? 8 : 16),
        color: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveSliderEndpointLabelColor()
                : Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
    },
    labelLeft: {
        composes: "slider__label--left",
        textAlign: "left",
    },
    labelMiddle: {
        composes: "slider__label--middle",
        textAlign: "center",
    },
    labelRight: {
        composes: "slider__label--right",
        textAlign: "right",
    },
    hybridSliderWrapper: {
        composes: "slider__hybrid-wrapper",
        "& .input-wrapper__label": {
            color: ({ theme }: { theme: any }) =>
                useModernSliderStyles(theme) ? resolveSliderEndpointLabelColor() : undefined,
        },
    },
    sliderContainer: {
        composes: "slider__container",
        marginLeft: ({ isHybridGrid }: { isHybridGrid: boolean }) => (isHybridGrid ? 10 : 0),
        marginRight: ({ isHybridGrid }: { isHybridGrid: boolean }) => (isHybridGrid ? 10 : 0),
        marginTop: ({ theme, hasImages }: { theme: any; hasImages: boolean }) => {
            if (useModernSliderStyles(theme)) {
                // Leave room above the track for the thumb value label (and images)
                // so it does not sit flush against the question text.
                return hasImages ? 90 : 20;
            }
            return hasImages ? 90 : Mixins.getSpacing(theme, 0, 30);
        },
        maxWidth: ({ theme, size }: { theme: any; size: string }) =>
            Mixins.inputMaxWidth(theme, size),
        minWidth: ({ theme, size }: { theme: any; size: string }) =>
            Mixins.inputMinWidth(theme, size),
        padding: [[0, 5]],
    },
    slider: {
        composes: "slider__compound",
        position: "relative",
        width: "100%",
        height: ({ theme, displayTicks }: { theme: any; displayTicks: boolean }) => {
            if (useModernSliderStyles(theme)) {
                return displayTicks ? "4em" : "2em";
            }
            return "6em";
        },
    },
    disabled: {
        opacity: 0.5,
        pointerEvents: "none",
    },
    rail: {
        backgroundColor: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveSliderTrackUnfilledColor()
                : theme.ControlBackground,
        borderColor: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? "transparent"
                : Mixins.borderColor(theme, theme.ControlBackground),
        borderRadius: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveModernSliderTrackBorderRadius(theme)
                : theme.BorderRadius / 4,
        borderStyle: "solid",
        borderWidth: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? 0 : theme.BorderWidth,
        bottom: 0,
        composes: "slider__rail",
        cursor: "pointer",
        height: ({ theme, isHybridGrid }: { theme: any; isHybridGrid: boolean }) =>
            useModernSliderStyles(theme) ? MODERN_SLIDER_TRACK_HEIGHT_PX : railHeight({ theme, isHybridGrid }),
        left: -5,
        margin: [["auto", 0]],
        marginLeft: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? 0 : -theme.BorderWidth,
        position: "absolute",
        right: -5,
        top: 0,
        zIndex: 1,
    },
    trackContainer: {
        composes: "slider__track-container",
        position: "absolute",
        top: 0,
        bottom: 0,
        left: -5,
        right: -5,
        margin: [["auto", 0]],
        pointerEvents: "none",
        zIndex: 1,
    },
    track: {
        composes: "slider__track",
        backgroundColor: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? resolveSliderTrackFilledColor(theme) : "transparent",
        // Filled portion sits inside the rail, so it takes the rail's radius (legacy's own track is
        // height 0 and therefore invisible, so its literal 0 carries no design intent).
        borderRadius: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? resolveModernSliderTrackBorderRadius(theme) : 0,
        bottom: 0,
        cursor: "pointer",
        height: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? MODERN_SLIDER_TRACK_HEIGHT_PX : 0,
        margin: [["auto", 0]],
        pointerEvents: ({ theme }: { theme: any }) => (useModernSliderStyles(theme) ? "auto" : "none"),
        position: "absolute",
        top: 0,
        zIndex: 1,
    },
    handleContainer: {
        composes: "slider__handle-container",
    },
    handle: {
        align: "center",
        backgroundColor: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveSliderHandleFillColor(theme)
                : theme.SecondaryColor,
        border: 0,
        borderColor: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveSliderHandleBorderColor(theme)
                : Mixins.borderColor(theme, theme.SecondaryColor),
        borderRadius: "50%",
        borderStyle: "solid",
        borderWidth: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveEmphasisBorderWidth(resolveFieldBorderWidth(theme))
                : theme.BorderWidth,
        bottom: 0,
        composes: "slider__handle",
        cursor: "pointer",
        height: ({ theme, isHybridGrid }: { theme: any; isHybridGrid: boolean }) =>
            useModernSliderStyles(theme)
                ? MODERN_SLIDER_HANDLE_SIZE_PX
                : handleSize({ theme, isHybridGrid }) - theme.BorderWidth,
        left: ({ percent }: { percent: number }) => `${percent}%`,
        margin: [["auto", 0]],
        // Centring must track the resolved border width, not the constant, or the handle drifts
        // off-centre once Field Settings → Borders moves away from its default.
        marginLeft: ({ theme, isHybridGrid }: { theme: any; isHybridGrid: boolean }) =>
            useModernSliderStyles(theme)
                ? ((MODERN_SLIDER_HANDLE_SIZE_PX +
                      resolveEmphasisBorderWidth(resolveFieldBorderWidth(theme))) /
                      2) *
                  -1
                : (handleSize({ theme, isHybridGrid }) / 2 + theme.BorderWidth) * -1,
        outline: "2px solid transparent",
        position: "absolute",
        textAlign: "center",
        top: 0,
        transition: "background 200ms, box-shadow 200ms, outline-color 200ms",
        userSelect: "none",
        width: ({ theme, isHybridGrid }: { theme: any; isHybridGrid: boolean }) =>
            useModernSliderStyles(theme)
                ? MODERN_SLIDER_HANDLE_SIZE_PX
                : handleSize({ theme, isHybridGrid }) - theme.BorderWidth,
        zIndex: 2,

        // Modern: keep base `2px solid transparent` on focus for forced-colors/HCM (do not set
        // outline:none — box-shadow alone is not painted in Windows High Contrast). Glow ring is
        // on :focus-visible only; legacy keeps the 22px SecondaryColor outline on :focus.
        "&:focus": {
            outlineColor: ({ theme }: { theme: any }) =>
                useModernSliderStyles(theme) ? undefined : alpha(theme.SecondaryColor, 0.5),
            outlineStyle: ({ theme }: { theme: any }) =>
                useModernSliderStyles(theme) ? undefined : "solid",
            outlineWidth: ({ theme }: { theme: any }) =>
                useModernSliderStyles(theme) ? undefined : "22px",
        },
        "&:focus-visible": {
            boxShadow: ({ theme }: { theme: any }) =>
                useModernSliderStyles(theme) ? resolveSliderFocusRingShadow(theme) : undefined,
        },
        "&:hover": {
            boxShadow: ({ theme }: { theme: any }) =>
                useModernSliderStyles(theme) ? resolveSliderFocusRingShadow(theme) : undefined,
        },
    },
    handleActive: {
        composes: "slider__handle--active",
        backgroundColor: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme)
                ? resolveSliderHandleFillColor(theme)
                : Mixins.darken(theme.SecondaryColor, theme.ActiveHighlightValue),
        boxShadow: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? resolveSliderFocusRingShadow(theme) : undefined,
    },
    handleLabelContainer: {
        bottom: "100%",
        color: "transparent",
        composes: "slider__handle-label-container",
        display: "inline-block",
        left: 0,
        marginLeft: "50%",
        position: "absolute",
        transform: "translateX(-50%)",
    },
    handleLabelContainerRegular: {
        composes: "slider__handle-label-container--regular",
        marginBottom: 16,
        padding: [[5, 16]],
    },
    handleLabelContainerHybridGrid: {
        composes: "slider__handle-label-container--hybrid-grid",
        marginBottom: 10,
        paddingTop: 2,
        paddingBottom: 0,
        paddingLeft: 8,
        paddingRight: 6,
    },
    handleLabelContainerModern: {
        composes: "slider__handle-label-container--modern",
        color: ({ theme }: { theme: any }) => resolveSliderHandleLabelColor(),
        marginBottom: 8,
        padding: 0,
    },
    handleLabelText: {
        color: ({ theme }: { theme: any }) =>
            Mixins.foregroundColor(
                theme,
                Mixins.compoundBackgroundColor(theme, theme.PrimaryColor),
            ),
        composes: "slider__handle-label-text",
        left: 0,
        position: "absolute",
        textAlign: "center",
        top: 0,
        zIndex: 4,
    },
    handleLabelTextDiscrete: {
        color: ({ theme }: { theme: any }) =>
            Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
        composes: "slider__handle-label-text-discrete",
        position: "relative",
        textAlign: "center",
        zIndex: 4,
        top: "110%",
    },
    handleLabelTextRegular: {
        composes: "slider__handle-label-text--regular",
        fontSize: "1rem",
        padding: [[5, 16]],
    },
    handleLabelTextHybridGrid: {
        composes: "slider__handle-label-text--hybrid-grid",
        fontSize: 14,
        paddingTop: 2,
        paddingBottom: 0,
        paddingLeft: 8,
        paddingRight: 0,
    },
    handleLabelTextModern: {
        composes: "slider__handle-label-text--modern",
        color: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? resolveSliderHandleLabelColor() : undefined,
        fontSize: "1rem",
        fontWeight: ({ theme }: { theme: any }) =>
            useModernSliderStyles(theme) ? resolveSliderHandleLabelFontWeight() : undefined,
        left: "auto",
        lineHeight: "1.25",
        padding: 0,
        position: "relative",
        top: "auto",
        zIndex: "auto",
    },
    handleLabelImageContainer: {
        boxSizing: "border-box",
        composes: "slider__handle-label-image-container",
        height: 84,
        margin: "auto",
        position: "relative",
        width: 150,
    },
    handleLabelImage: {
        backgroundImage: ({ imageUrl }: { imageUrl: string }) => `url("${imageUrl}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        bottom: 0,
        composes: "slider__handle-label-image",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 2,
    },
    handleLabelBackground: {
        align: "center",
        background: ({ theme, isDiscrete }: { theme: any; isDiscrete: boolean }) =>
            Mixins.alpha(isDiscrete ? theme.ControlBackground : theme.PrimaryColor),
        borderRadius: ({ theme }: { theme: any }) => theme.BorderRadius / 2,
        bottom: 0,
        boxShadow: ({ theme, isDiscrete }: { theme: any; isDiscrete: boolean }) =>
            `0 0 0 ${theme.BorderWidth}px ${Mixins.borderColor(
                theme,
                isDiscrete ? theme.ControlBackground : theme.PrimaryColor,
            )}`,
        composes: "slider__handle-label-background",
        left: 0,
        opacity: ({ theme }: { theme: any }) => theme.ControlAlpha,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 2,
    },
    handleLabelArrowBorder: {
        border: "8px solid transparent",
        borderTopColor: ({ theme, isDiscrete }: { theme: any; isDiscrete: boolean }) =>
            Mixins.borderColor(theme, isDiscrete ? theme.ControlBackground : theme.PrimaryColor),
        content: '""',
        left: "50%",
        marginLeft: -8,
        marginTop: ({ theme }: { theme: any }) => theme.BorderWidth - 2,
        opacity: ({ theme }: { theme: any }) => theme.ControlAlpha,
        position: "absolute",
        top: "100%",
        zIndex: 1,
    },
    handleLabelArrow: {
        border: "8px solid transparent",
        borderTopColor: ({ theme, isDiscrete }: { theme: any; isDiscrete: boolean }) =>
            isDiscrete ? theme.ControlBackground : theme.PrimaryColor,
        left: "50%",
        marginLeft: -8,
        marginTop: -2,
        opacity: ({ theme }: { theme: any }) => theme.ControlAlpha,
        position: "absolute",
        top: "100%",
        zIndex: 3,
    },
    tickContainer: {
        composes: "slider__tick-container",
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        margin: [["auto", 0]],
        height: 10,
        cursor: "default",
        userSelect: "none",
        zIndex: 0,
    },
    tick: {
        composes: "slider__tick",
        position: "absolute",
        marginLeft: -0.5,
        top: "100%",
        marginTop: ({ theme }: { theme: any }) => theme.BorderWidth,
        width: 1,
        height: 13,
        left: ({ percent }: { percent: number }) => `${percent}%`,
        backgroundColor: ({ theme }: { theme: any }) =>
            Mixins.borderColor(theme, theme.ControlBackground),
    },
    tickLabel: {
        composes: "slider__tick-label",
        cursor: "pointer",
        position: "absolute",
        top: "100%",
        marginTop: 9,
        padding: [[9, 0]],
        overflowWrap: "anywhere",
        textAlign: "center",
        color: ({ theme }: { theme: any }) =>
            Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
    },
    tickLabelRegular: {
        composes: "slider__tick-label--regular",
        fontSize: ({ theme }: { theme: any }) => theme.UseModernTheme ? Mixins.getFontSize(theme.TextFontSize) : "18px",
        lineHeight: "18px",
        marginLeft: ({ count, percent }: { count: number; percent: number }) => {
            if (percent === 0 && count > 1) {
                return -20;
            }
            if (percent === 100 && count > 1) {
                return 4;
            }
            return `${-(100 / count) / 2}%`;
        },
        width: ({ count, percent }: { count: number; percent: number }) => {
            if ([0, 100].includes(percent) && count > 1) {
                return `${90 / count / 2}%`;
            }
            return `${100 / count}%`;
        },
        left: ({ count, percent }: { count: number; percent: number }) => {
            if (percent === 100 && count > 1) {
                return `${percent - 70 / count / 2}%`;
            }
            return `${percent}%`;
        },
    },
    tickLabelHybridGrid: {
        composes: "slider__tick-label--hybrid-grid",
        fontSize: 14,
        lineHeight: "14px",
        marginLeft: ({ count }: { count: number }) => `${-(100 / count) / 2}%`,
        width: ({ count }: { count: number }) => `${100 / count}%`,
        left: ({ percent }: { percent: number }) => `${percent}%`,
    },
    noAnswer: {
        composes: "slider__no-answer",
        marginTop: 6,
        outline: "none",
        "& > div": {
            flex: "initial",
        },
    },
    noAnswerLabel: {
        composes: "slider__no-answer__label",
        marginLeft: 10,
        userSelect: "none",
    },
    clearButtonModern: {
        composes: "slider__button--clear--modern",
        marginTop: 8,
    },
    srOnly: {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        border: 0,
    },
} as any);
